import { BuyOutcome, DealsContext, PurchaseService, vipPageView } from "@highmarch/app";
import { economyLink } from "../ui/hub/economyLink";
import { DealBuyOutcome, DealsLink, dealsLink, setDealsLink } from "../ui/deals/dealsLink";
import { openDeals } from "../ui/deals/dealsSheet";
import { crestUrl, openVip } from "../ui/deals/vipSheet";
import { vipDot } from "../ui/vip/vipModel";
import { uiUrl } from "../ui/portraits";
import { rowHtml } from "../ui/components";
import { bindFarmButton } from "../ui/hub/battleCard";
import type { Feature, HubApi } from "./registry";
import { ECONOMY_FROM_CLEARED } from "./economy";
import "../ui/styles/deals.css";

/**
 * Deals & VIP in the hub (docs/SHOP.md §5.2): the "Deals" pill in the top bar (a chest and a red count for reached
 * Weekly milestones and a ready VIP chest — a badge, never a pop-up), the gems pill's "+" opening Deals → Gems, the
 * More → VIP row (free, everywhere) and the link the Deals screen buys through. Paid pages show only with a store
 * (StoreKit in the iOS app; the web's sandbox with ?debug or ?sandboxstore); nothing here is reachable from a level or
 * the end screen (no in-level row; the end screen's one VIP line is the battle chest's production bonus, a fact with
 * no button or link).
 *
 * The rules are @highmarch/app's: the catalog's `Deals` (LevelCatalog.deals) and the economy feature's `PurchaseService`
 * (features/economy.ts), which owns the store — `buyDeal` (intent → save → store → grant once → save → finish), the
 * store's prices (`storeProducts`), where paid offers show (`dealsContext`, the storefront) and every late
 * transaction (Ask to Buy, unfinished at launch, Restore). The day's VIP login points are the economy feature's too.
 */

/** The Deals pill shows from here: the First Command and Daily Deals open after level 3 (offers.json unlockAfterLevel). */
export const DEALS_FROM_CLEARED = 3;
/** Top-bar order: after gems (10) and Marks (20). */
const DEALS_PILL_ORDER = 30;
/** More-sheet slot for VIP: after the Market (20), before Achievements (30). */
const VIP_ROW_ORDER = 25;
/** End-screen block order: the VIP production line sits right under the chest, before the event block (20). */
const VIP_END_ORDER = 10;
/** No store (the web build without ?debug/?sandboxstore): no paid offer anywhere. */
const NO_PAID: DealsContext = { paidAllowed: false, storefront: null };

/** A `buyDeal` outcome as the Deals screen shows it. */
function shown(r: Awaited<ReturnType<PurchaseService["buyDeal"]>>): DealBuyOutcome {
  return r.status === "granted" ? { status: "granted", granted: r.grant.granted, vipPoints: r.grant.vipPoints, bankedGear: r.grant.bankedGear } : r;
}

/** `purchases`: the economy feature's PurchaseService (null without a store); a test passes its own. */
export function createDealsFeature(purchases: () => PurchaseService | null = () => economyLink()?.purchases ?? null): Feature {
  return {
    id: "deals",
    register(ctx, registry) {
      const context = (): DealsContext => purchases()?.dealsContext() ?? NO_PAID;
      let prices: Promise<ReadonlyMap<string, string>> | null = null;
      const link: DealsLink = {
        get deals() { return ctx.catalog.deals; },
        catalog: ctx.catalog, now: ctx.now, ctx: context, hub: null,
        get storeKind() { return economyLink()?.storeKind ?? null; },
        prices: () => {
          const p = purchases();
          if (!p) return Promise.resolve(new Map());
          prices ??= p.storeProducts()
            .then((list) => new Map([...list].map(([id, product]) => [id, product.displayPrice] as const)))
            .catch(() => { prices = null; return new Map<string, string>(); });
          return prices;
        },
        buy: async (offerId) => {
          const p = purchases();
          return p ? shown(await p.buyDeal(offerId, context(), "hub")) : { status: "refused", reason: "not on sale" };
        },
        gemPacks: () => (purchases() ? ctx.balance.commerce.shop.gemPacks : []),
        buyGems: (packId): Promise<BuyOutcome> => purchases()?.buy(packId, "hub") ?? Promise.resolve({ status: "refused", reason: "not on sale" }),
        changed: () => { if (link.hub) link.hub.changed(); else ctx.persist(); },
      };
      setDealsLink(link);
      const withHub = (hub: HubApi) => { link.hub = hub; return link; };
      const showDeals = () => context().paidAllowed && ctx.catalog.highestLevelCleared >= DEALS_FROM_CLEARED;

      // Shop redesign (2026-10-19): the Store opens from the gems button (features/economy.ts); VIP sits beside it.
      registry.topBarPill({
        id: "vip", order: DEALS_PILL_ORDER,
        html: () => {
          if (ctx.catalog.highestLevelCleared < ECONOMY_FROM_CLEARED) return "";
          const v = vipPageView(ctx.catalog.deals, ctx.now(), context()), n = vipDot(v);
          return `<span class="vipbtn"><img decoding="sync" src="${uiUrl("icons/vip_points.png")}" alt=""><b class="disp">VIP ${v.level}</b>${n > 0 ? `<i class="dot">${n}</i>` : ""}</span>`;
        },
        onTap: (hub) => { withHub(hub); openVip(); },
      });
      registry.moreRow({
        id: "vip", order: VIP_ROW_ORDER, icon: "icons/deals_vip.png", home: "shop",   // M35: Shop → Gems → VIP
        // VIP v2 (docs/SHOP.md §8): the page's own view, so the row, the Deals chip and the page agree.
        label: () => `VIP ${vipPageView(ctx.catalog.deals, ctx.now(), context()).level}`,
        note: () => { const v = vipPageView(ctx.catalog.deals, ctx.now(), context()); return v.nextAt === null ? "Maxed · a free bundle every day" : `${v.xp.toLocaleString("en-US")} / ${v.nextAt.toLocaleString("en-US")} VIP XP`; },
        dot: () => vipDot(vipPageView(ctx.catalog.deals, ctx.now(), context())),
        // Free and store-less (the web build too); it opens with the Shop and the Market, after the first clear.
        visible: () => ctx.catalog.highestLevelCleared >= ECONOMY_FROM_CLEARED,
        open: (hub) => { withHub(hub); openVip(); },
      });
      // M35: VIP's red count (a free bundle or chest to take) moved with its row to the Shop tab.
      registry.tabBadge({ tab: "shop", count: () => (ctx.catalog.highestLevelCleared >= ECONOMY_FROM_CLEARED ? vipDot(vipPageView(ctx.catalog.deals, ctx.now(), context())) : 0) });
      registry.hubWidget({
        id: "deals",
        mount: (hub) => { withHub(hub); return { dispose: () => { if (dealsLink()?.hub === hub) link.hub = null; } }; },
      });
      // The battle chest's VIP resource production (docs/SHOP.md §8.3): what the level-end bank (boot/levelFlow.ts,
      // bankChest with production) adds on top of the chest's own roll, so the hub's pills add up. Zero at VIP 0: no line.
      registry.endScreenExtra({
        id: "vipProduction", order: VIP_END_ORDER,
        html: (f) => {
          const chest = f.sim.chest;
          if (!chest) return "";
          const n = ctx.catalog.productionBonus(chest);
          if (n <= 0) return "";
          return rowHtml({ lead: crestUrl(ctx.catalog.profile.vip.crest), title: `+${n.toLocaleString("en-US")}`, sub: `gold · VIP ${ctx.catalog.deals.vipLevel()}`, compact: true, className: "erow vipprod gain" });
        },
      });
      // FARM L{n} on the battle card (docs/SHOP.md K6): a replay started the way PLAY starts one.
      registry.hubWidget({ id: "farm", mount: (hub) => ({ dispose: bindFarmButton(hub.parent, (level) => hub.play(level)) }) });
      if (ctx.debug) {
        (window as unknown as { __tdDeals: unknown }).__tdDeals = {
          deals: ctx.catalog.deals, link, open: (page?: string) => openDeals({ page }), vip: () => openVip(), crest: crestUrl,
          /** Captures: VIP XP without paying (re-syncs the benefits). */
          addVip: (xp: number) => { ctx.catalog.deals.addVipXp(xp); link.changed(); },
        };
      }
    },
  };
}

export const dealsFeature: Feature = createDealsFeature();
